import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, ArrowRight } from "lucide-react";
// ── Use the SAME Web App URL as your other Provinyx forms (Contact/Demo) ──────
// This way every submission writes into the same spreadsheet, just a different tab.
const PRICING_SHEET_URL = "https://script.google.com/macros/s/AKfycbw4WkH2CqSLHv63eFmGubKJmOrbIghcoL5YX26Tsc9szhNwxAxTzS4JWHNYV6KkgMHh/exec";
// ─────────────────────────────────────────────────────────────────────────────


const pricingPlans = [
  {
    name: "Standard",
    target: "Pilots, demos, and for the proof of concept for free",
    price: "$0",
    period: "No credit card required",
    periodInline: false,
    limit: "Up to 50 DPPs",
    buttonText: "Get Started",
    isPrimary: false,
    features: [
      "Basic DPP dashboard & QR access",
      "Limited passport publishing",
      "Basic product data hosting",
      "Basic reporting & analytics",
    ],
  },
  {
    name: "Basic",
    target: "Small brands and early commercial launches",
    price: "$99",
    period: "/ month",
    periodInline: true,
    limit: "1,000 DPPs",
    buttonText: "Contact Sales",
    isPrimary: true,
    features: [
      "All Standard features",
      "Full product data hosting",
      "Standard support & governance controls",
      "Limited AI-assisted content support",
      "Limited multi-user access",
      "Limited integrations",
    ],
  },
  {
    name: "Premium",
    target: "Growing brands with multiple lines of products",
    price: "$499",
    period: "/ month",
    periodInline: true,
    limit: "10,000 DPPs",
    buttonText: "Contact Sales",
    isPrimary: false,
    features: [
      "All Basic features",
      "Full AI-assisted content support",
      "Full multi-user access",
      "Expanded reporting & analytics",
      "Limited to moderate integrations",
      "Governance controls",
      "Dedicated support",
    ],
  },
  {
    name: "Enterprise",
    target: "Large enterprises and complex deployments",
    price: "Contact Sales for Price",
    period: "",
    periodInline: false,
    limit: "No set limit",
    buttonText: "Contact Sales",
    isPrimary: false,
    features: [
      "All Premium features",
      "Advanced reporting & analytics",
      "Full ERP, workflow & consortium integration",
      "Governance controls",
      "Prioritised support & dedicated onboarding",
    ],
  },
];

// `free` key = the Standard column (key name kept so the table rendering below doesn't change)
const comparisonData = [
  { feature: "DPP limit", free: "50", basic: "1,000", premium: "10,000", enterprise: "No set limit" },
  { feature: "QR-enabled product passports", free: "Yes", basic: "Yes", premium: "Yes", enterprise: "Yes" },
  { feature: "Product data hosting", free: "Basic", basic: "Yes", premium: "Yes", enterprise: "Yes" },
  { feature: "AI-assisted content support", free: "No", basic: "Limited", premium: "Yes", enterprise: "Yes" },
  { feature: "Multi-user access", free: "No", basic: "Limited", premium: "Yes", enterprise: "Yes" },
  { feature: "Reporting and analytics", free: "Basic", basic: "Basic", premium: "Expanded", enterprise: "Advanced" },
  { feature: "Integrations", free: "No", basic: "Limited", premium: "Limited to moderate", enterprise: "Full ERP, workflow and consortium integration" },
  { feature: "Dedicated support", free: "No", basic: "No", premium: "Yes", enterprise: "Yes" },
];

const renderCell = (val) => {
  if (val === "Yes") {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="bg-blue-100 rounded-full p-1.5">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
      </div>
    );
  }
  if (val === "No") {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="bg-gray-100 rounded-full p-1.5">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
      </div>
    );
  }
  return <div className="text-center text-sm text-gray-700 font-semibold">{val}</div>;
};

const PlanModal = ({ isOpen, onClose, selectedPlan, setSelectedPlan }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", country: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState("");

  if (!isOpen) return null;
  const plans = ["Standard", "Basic", "Premium", "Enterprise"];
  const isStandardSelected = selectedPlan === "Standard";

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleClose = () => {
    setSubmitted(false);
    setForm({ name: "", phone: "", email: "", country: "" });
    setError("");
    onClose();
  };

  const handleOnboardNow = () => {
    handleClose();
    navigate("/onboarding");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.country) {
      setError("Please fill in all fields.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        type: "Pricing",
        plan: selectedPlan,
        name: form.name,
        phone: form.phone,
        email: form.email,
        country: form.country,
        timestamp: new Date().toISOString(),
      });
      await fetch(`${PRICING_SHEET_URL}?${params.toString()}`, { method: "GET", mode: "no-cors" });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-[#1e2330] w-full max-w-md rounded-2xl relative shadow-2xl p-6 md:p-8 animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 bg-gray-700/50 hover:bg-gray-600 text-gray-300 hover:text-white rounded-full p-2 transition-colors focus:outline-none"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {!submitted ? (
          <>
            <h2 className="text-3xl font-bold text-white text-center mb-1">
              {selectedPlan} Plan
            </h2>
            <p className="text-gray-400 text-center mb-6 text-sm">
              {isStandardSelected ? "No form needed - just onboard" : "Fill in your Details"}
            </p>

            <div className="flex bg-[#2a3040] rounded-xl p-1 mb-6">
              {plans.map((plan) => (
                <button
                  key={plan}
                  onClick={() => setSelectedPlan(plan)}
                  className={`flex-1 py-2 px-1 text-[10px] sm:text-xs font-semibold rounded-lg transition-all duration-200 focus:outline-none ${
                    selectedPlan === plan
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-gray-400 hover:text-gray-200"
                  }`}
                >
                  {plan}
                </button>
              ))}
            </div>

            {isStandardSelected ? (
              /* ── Standard plan: no form, direct onboarding CTA ── */
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-blue-600/15 flex items-center justify-center mx-auto mb-5">
                  <Rocket className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-white text-lg font-bold mb-2">
                  You're ready to get started - for free
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-7 px-2">
                  Set up your company workspace in a few quick steps and start using your Standard plan.
                </p>
                <button
                  onClick={handleOnboardNow}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  Onboard Now <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              /* ── Paid plans: lead-capture form ── */
              <form className="space-y-4" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full bg-[#2a3040] border border-transparent text-white rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder-gray-500"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your Phone Number"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full bg-[#2a3040] border border-transparent text-white rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder-gray-500"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Your mail address"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-[#2a3040] border border-transparent text-white rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder-gray-500"
                />
                <input
                  type="text"
                  name="country"
                  placeholder="Country"
                  value={form.country}
                  onChange={handleChange}
                  className="w-full bg-[#2a3040] border border-transparent text-white rounded-lg px-4 py-3 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder-gray-500"
                />

                {error && <p className="text-red-400 text-xs text-center">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-white text-black font-bold py-3 rounded-lg mt-4 hover:bg-gray-100 transition-colors focus:outline-none disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Submit"}
                </button>
              </form>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-white text-xl font-bold mb-2">We'll be in touch!</h3>
            <p className="text-gray-400 text-sm">
              Thanks for your interest in the {selectedPlan} plan. Our team will reach out shortly.
            </p>
            <button
              onClick={handleClose}
              className="mt-7 px-8 py-3 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CompareModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-5xl rounded-2xl relative shadow-2xl flex flex-col max-h-[90vh] animate-fadeIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Plan Comparison</h2>
          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full p-2 transition-colors focus:outline-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-x-auto overflow-y-auto p-0 m-0 w-full flex-grow">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
              <tr>
                <th className="p-4 sm:p-5 font-bold text-gray-900 w-[150px] min-w-[150px] sm:w-[200px] sm:min-w-[200px] lg:w-1/3 lg:min-w-auto sticky left-0 bg-gray-50 z-30 border-r border-gray-200">
                  Feature
                </th>
                <th className="p-4 sm:p-5 font-bold text-gray-900 text-center w-[140px] min-w-[140px] lg:w-1/6 lg:min-w-auto">Standard</th>
                <th className="p-4 sm:p-5 font-bold text-gray-900 text-center w-[140px] min-w-[140px] lg:w-1/6 lg:min-w-auto text-blue-600">Basic</th>
                <th className="p-4 sm:p-5 font-bold text-gray-900 text-center w-[140px] min-w-[140px] lg:w-1/6 lg:min-w-auto">Premium</th>
                <th className="p-4 sm:p-5 font-bold text-gray-900 text-center w-[140px] min-w-[140px] lg:w-1/6 lg:min-w-auto">Enterprise</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {comparisonData.map((row, idx) => (
                <tr key={idx} className="hover:bg-blue-50/30 transition-colors">
                  <td className="p-4 sm:p-5 text-sm font-semibold text-gray-800 sticky left-0 bg-white z-10 border-r border-gray-200 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)] lg:shadow-none">
                    {row.feature}
                  </td>
                  <td className="p-4 sm:p-5">{renderCell(row.free)}</td>
                  <td className="p-4 sm:p-5 bg-blue-50/10">{renderCell(row.basic)}</td>
                  <td className="p-4 sm:p-5">{renderCell(row.premium)}</td>
                  <td className="p-4 sm:p-5">{renderCell(row.enterprise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PricingCard = ({ plan, onOpenModal }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleFeatures = isExpanded ? plan.features : plan.features.slice(0, 3);
  const isStandard = plan.name === "Standard";

  const handleButtonClick = () => {
    if (isStandard) {
      navigate("/onboarding");
    } else {
      onOpenModal(plan.name);
    }
  };

  return (
    <div
      className={`relative flex flex-col h-auto rounded-2xl bg-white p-5 lg:p-6 transition-transform duration-300 hover:scale-105 shadow-sm ${
        plan.isPrimary ? "border-blue-600 border-2" : "border-gray-200 border"
      }`}
    >
      {plan.isPrimary && (
        <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-xl">
          MOST POPULAR
        </div>
      )}

      <div className="mb-4 mt-2">
        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
        <p className="text-sm text-gray-500 mt-1 h-10 leading-tight">{plan.target}</p>
      </div>

      <div className="mb-4 h-16 flex flex-col justify-start text-gray-900">
        <div className="flex items-baseline flex-wrap">
          <span className={`font-extrabold tracking-tight ${plan.price.length > 10 ? 'text-[22px] sm:text-2xl leading-tight' : 'text-4xl'}`}>
            {plan.price}
          </span>
          {plan.periodInline && plan.period && (
            <span className="ml-1 text-sm font-medium text-gray-500">{plan.period}</span>
          )}
        </div>
        {!plan.periodInline && plan.period && (
          <span className="text-sm font-medium text-gray-500 mt-1">{plan.period}</span>
        )}
      </div>

      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
        <p className="text-gray-500 mb-1">DPP limit</p>
        <p className="font-semibold text-gray-900">{plan.limit}</p>
      </div>

      <button
        onClick={handleButtonClick}
        className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-colors mb-5 focus:outline-none ${
          plan.isPrimary
            ? "bg-blue-600 text-white hover:bg-blue-700"
            : "bg-black text-white hover:bg-gray-800"
        }`}
      >
        {isStandard ? "Onboard now" : plan.buttonText}
      </button>

      <div className="flex-col flex-grow">
        <p className="text-sm font-semibold text-gray-900 mb-3">What's included:</p>

        <ul className={`space-y-3 text-sm text-gray-600 mb-4 ${!isExpanded ? "min-h-[120px]" : ""}`}>
          {visibleFeatures.map((feature, index) => (
            <li key={index} className="flex items-start">
              <svg className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {plan.features.length > 3 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-blue-600 text-sm font-medium hover:underline focus:outline-none"
          >
            {isExpanded ? "Read Less ↑" : "Read More ↓"}
          </button>
        )}
      </div>
    </div>
  );
};

const Pricing = ({ sectionClassName = "py-20" }) => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState("Basic");
  const [activeMobilePlan, setActiveMobilePlan] = useState("Standard");

  const handleOpenFormModal = (planName) => {
    setSelectedPlan(planName);
    setIsFormModalOpen(true);
  };

  return (
    <section className={`${sectionClassName} bg-gray-50 relative`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">
            Flexible Pricing
          </span>
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Choose the Right Plan for Your Needs
          </h2>
          <p className="mt-4 text-lg text-gray-500">
            From individual product authentication to enterprise-grade DPP solutions, we have a plan that scales with your business.
          </p>
        </div>

        {/* Mobile only: single card + tabs */}
        <div className="block md:hidden max-w-md mx-auto">
          <div className="flex bg-gray-200 rounded-xl p-1 mb-8 overflow-x-auto shadow-inner">
            {pricingPlans.map((plan) => (
              <button
                key={plan.name}
                onClick={() => setActiveMobilePlan(plan.name)}
                className={`flex-1 py-2 px-3 text-xs sm:text-sm font-semibold rounded-lg transition-all whitespace-nowrap focus:outline-none ${
                  activeMobilePlan === plan.name
                    ? "bg-white text-blue-600 shadow-sm border border-gray-100"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {plan.name}
              </button>
            ))}
          </div>
          <PricingCard
            plan={pricingPlans.find((p) => p.name === activeMobilePlan)}
            onOpenModal={handleOpenFormModal}
          />
        </div>

        {/* Tablet only: 2-column grid - makes better use of the wider screen than the single-card mobile view */}
        <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-6">
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={index}
              plan={plan}
              onOpenModal={handleOpenFormModal}
            />
          ))}
        </div>

        {/* Desktop only: full 4-column grid */}
        <div className="hidden lg:grid lg:grid-cols-4 gap-6 items-start">
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={index}
              plan={plan}
              onOpenModal={handleOpenFormModal}
            />
          ))}
        </div>

        <div className="flex justify-center mt-12 mb-6">
          <button
            onClick={() => setIsCompareModalOpen(true)}
            className="bg-white border-2 border-blue-600 text-gray-800 px-8 py-3 rounded-xl font-bold hover:text-blue-600 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 focus:outline-none"
          >
            Compare all four plans
          </button>
        </div>

        <div className="mt-8 text-center text-sm text-gray-500">
          Need a custom solution?{" "}
          <button
            onClick={(e) => {
              e.preventDefault();
              handleOpenFormModal("Basic");
            }}
            className="font-medium text-blue-600 hover:underline focus:outline-none"
          >
            Contact our sales team
          </button>
        </div>
      </div>

      <PlanModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
      />

      <CompareModal
        isOpen={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
      />
    </section>
  );
};

export default Pricing;